import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { LocalStorageService } from 'src/app/services/local-storage.service';
import { SweetAlertService } from './../../../../services/sweetalert.service';
import { WebinarService } from 'src/app/services/shared/webinar.service';
import { PaymentService } from 'src/app/services/shared/payment.service';
import Swal from 'sweetalert2';
import { callbackUrls } from 'src/app/utils/apiUrls';

declare var Razorpay: any;

@Component({
  selector: 'app-register-webinar',
  templateUrl: './register-webinar.component.html',
  styleUrls: ['./register-webinar.component.scss'],
})
export class RegisterWebinarComponent implements OnInit, OnDestroy {
  registerForm!: FormGroup;
  userDetails: any;
  webinarId: string;
  userId: string = '';
  clicked: boolean = false;
  loading: boolean = false;
  isReadyToPay: boolean;
  amount: number;
  isCME: boolean = false; 
  private paymentOrderResponse: any = {};
  private rzp: any;
  private paymentInProgress: boolean = false;
  private paymentMethod: string | null = null;

  constructor(
    private fb: FormBuilder,
    private localStorageService: LocalStorageService,
    private sweetAlertService: SweetAlertService,
    private dialogRef: MatDialogRef<RegisterWebinarComponent>,
    private webinarService: WebinarService,
    private paymentService: PaymentService,
   @Inject(MAT_DIALOG_DATA) public data: { webinarId: string; paid: boolean; amount: number; flag?: boolean }
  ) {
    this.webinarId = data.webinarId;
    this.isReadyToPay = data.paid;
    this.amount = data.amount;
    this.isCME = data.flag || false;
    this.initializeForm();
  }

  ngOnInit(): void {
    this.userDetails = this.localStorageService.getItem('userDetails');
    if (this.userDetails) {
      this.userDetails = JSON.parse(this.userDetails);
      this.userId = this.userDetails.userId || '';
      this.registerForm.patchValue({
        name: this.userDetails.userName || '',
        email: this.userDetails.email || '',
        phone: this.userDetails.phone || '',
      });
    }
  }

  ngOnDestroy(): void {
    this.closeRazorpayPopup();
  }

  private initializeForm(): void {
    this.registerForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.pattern('^[0-9]{5,15}$')]],
      registered: [{ value: '', disabled: !this.isCME }, Validators.required],
      customerAddress: [{ value: '', disabled: !this.isCME }, Validators.required],
    });
  }

  submit(): void {
    this.clicked = true;
    if (this.registerForm.valid) {
      this.checkEligibility();
    }
  }

  private checkEligibility(): void {
    this.loading = true;
    this.webinarService.checkEligibility(this.webinarId, this.registerForm.value.email).subscribe({
      next: (res: any) => {
        this.loading = false;
        if (res.data) {
          this.isReadyToPay ? this.initiatePayment() : this.registerWebinar();
        } else {
          this.sweetAlertService.showError('Registration Failed', res.message);
        }
      },
      error: (error: any) => {
        this.handleError(error, 'Registration failed!!');
      },
    });
  }

  private registerWebinar(): void {
    const regData = {
      customerName: this.registerForm.value.name,
      email: this.registerForm.value.email,
      phoneNumber: String(this.registerForm.value.phone),
      webinarPostId: this.webinarId,
      userId: this.userId,
      registeredNumber: this.registerForm.value.registered,
      customerAddress: this.registerForm.value.customerAddress,
    };

    this.webinarService.register(regData).subscribe({
      next: () => {
        if (!this.isReadyToPay) {
          this.showSuccessAlert();
        }
        this.dialogRef.close(this.isReadyToPay ? "isNotPayment" : true);
      },
      error: (error: any) => {
        this.handleError(error, 'Registration failed!!');
      },
    });
  }

  private initiatePayment(): void {
    this.paymentInProgress = true;
    this.loading = true;

    const payload = {
      customerName: this.registerForm.value.name,
      email: this.registerForm.value.email,
      phoneNumber: String(this.registerForm.value.phone),
      webinarPostId: this.webinarId,
      userId: this.userId,
      registeredNumber: this.registerForm.value.registered,
      customerAddress: this.registerForm.value.customerAddress,
    };

    this.paymentService.createOrder(this.webinarId, payload).subscribe({
      next: (res: any) => {
        this.paymentOrderResponse = res.data;
        this.loading = false;
        this.openRazorpay();
      },
      error: () => {
        this.handlePaymentError('Unable to create order.');
      },
    });
  }

  private openRazorpay(): void {
    if (!(window as any).Razorpay) {
      this.handlePaymentError('Razorpay SDK is not available.');
      return;
    }
  
    const options = {
      key: this.paymentOrderResponse.keyId,
      amount: this.paymentOrderResponse.amount,
      currency: this.paymentOrderResponse.currency,
      order_id: this.paymentOrderResponse.orderId,
      // image: '../../../../../assets/images/logomedworld32full.svg',
      
      // Razorpay will redirect to this page after payment
      // callback_url: `https://dev.medworldexpo.com/payment-status?order_id=${this.paymentOrderResponse.orderId}`,
      callback_url :`${callbackUrls.callbackhandler}?order_id=${this.paymentOrderResponse.orderId}&webinar_id=${this.webinarId}`,
      redirect: true, // Must be set to true for callback_url to work
      
  
      prefill: {
        name: this.registerForm.value.name,
        email: this.registerForm.value.email,
        contact: this.registerForm.value.phone,
      },
      notes: { address: 'Razorpay Corporate Office' },
      theme: { color: '#3399cc' },
      
    };
  
    try {
      this.rzp = new (window as any).Razorpay(options);
      this.rzp.open();
    } catch (error) {
      this.handlePaymentError('Unable to open payment popup.');
    }
  }
  

  private closeRazorpayPopup(): void {
    if (this.rzp && typeof this.rzp.close === 'function') {
      try {
        this.rzp.close();
      } catch (error) {
         // Silent error handling
      }
    }
  }

  private handleError(error: any, title: string): void {
    this.loading = false;
    this.paymentInProgress = false;
    if (error.status === 409) {
      const message =
        error.error.message === 'Participants limit exceeded! We will let you know once slots are available.'
          ? 'Participants limit exceeded! We will let you know once slots are available.'
          : 'You have already registered.';
      this.sweetAlertService.showError(title, message);
    } else if (error.status === 400 && error.error.message.includes('payment')) {
      this.sweetAlertService.showError(title, 'Payment not confirmed. Please try again or contact support.');
    } else {
      this.sweetAlertService.showError(title, 'An unexpected error occurred.');
    }
  }

  private handlePaymentError(message: string): void {
    this.loading = false;
    this.paymentInProgress = false;
    this.closeRazorpayPopup();
    Swal.close();
    this.sweetAlertService.showError('Payment Failed', message);
  }

  private showSuccessAlert(): void {
    Swal.fire({
      html: this.isCME ? 'You have successfully registered for the CME. The CME link and details will be sent to your email address.' : 'You have successfully registered for the webinar. The Webinar link and details will be sent to your email address.',
      confirmButtonText: 'Close',
      customClass: {
        htmlContainer: 'swal2-html-container',
        popup: 'swal2-popup',
      },
      didOpen: () => {
        const container = Swal.getHtmlContainer();
        if (container) {
          Object.assign(container.style, {
            color: '#0D87C8',
            fontWeight: '500',
            fontSize: '24px',
            lineHeight: '36px',
          });
        }
        const popup = Swal.getPopup();
        if (popup) {
          Object.assign(popup.style, {
            borderRadius: '30px',
            boxShadow: '0px 10px #94C743',
          });
        }
      },
    });
  }

  onClose(): void {
    this.dialogRef.close("isNotPayment");
  }
}